import { useCallback, useEffect, useRef, useState } from 'react';
import { isElectronServerRuntime } from '@/lib/electronBridge';

/**
 * Tell the people at the institute what the update is doing, and let them
 * install it.
 *
 * Until recently nobody at an institute could see any of this. The updater
 * downloads an installer, verifies it, and then tries to run it silently --
 * which Windows refuses when the app sits in Program Files, because a program
 * there cannot replace itself without administrator rights. The attempt is
 * abandoned, the EXE carries on happily on its old version, and the only
 * record is a field in a heartbeat a superadmin would have to go looking for.
 * One institute was nine releases behind for exactly that reason.
 *
 * The first version of this banner fixed the "nobody is told" half and left a
 * worse complaint behind: "they press update now but there is no progress bar
 * or anything, where it is downloaded, where the percentage is". All three were
 * fair, and none of them needed new data -- selfUpdate.status() has reported
 * percent, downloaded/total bytes, pending_dir and staged_installer all along.
 * What it had was:
 *
 *   - a 60-second poll, so a 79 MB download advanced its number once a minute
 *     and read, correctly, as frozen;
 *   - a percentage in prose and no bar;
 *   - nothing said about where the file went, so a blocked install looked like
 *     a download that had to start over;
 *   - output only while phase === 'downloading', so checking, verifying and
 *     installing showed nothing at all.
 *
 * So the poll follows the work -- about a second while something is happening,
 * a minute when it is not -- and every phase says what it is doing.
 *
 * Shown only in the Institute EXE, and only when there is something to say.
 */

type UpdateState = {
    phase?: string;
    current_version?: string | null;
    available_version?: string | null;
    staged_file_name?: string | null;
    staged_installer?: string | null;
    pending_dir?: string | null;
    can_install_manually?: boolean;
    admin_rights_required?: boolean;
    install_blocked?: string | null;
    install_failures?: number;
    install_deferred_reason?: string | null;
    error?: string | null;
    percent?: number | null;
    downloaded_mb?: number;
    total_mb?: number;
};

type Bridge = {
    updateStatus?: () => Promise<UpdateState>;
    installUpdate?: (opts?: { elevate?: boolean }) => Promise<{ ok?: boolean; error?: string; opened?: boolean }>;
};

// While something is moving, a number that updates once a minute is
// indistinguishable from one that has stopped.
const POLL_BUSY_MS = 1_200;
const POLL_IDLE_MS = 60_000;
const BUSY_PHASES = new Set(['checking', 'downloading', 'installing']);

/** A bar, because a percentage on its own does not read as movement. */
function Bar({ percent, indeterminate }: { percent: number | null; indeterminate?: boolean }) {
    return (
        <div
            className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={indeterminate || percent === null ? undefined : percent}
        >
            <div
                className={`h-full rounded-full bg-primary-600 transition-[width] duration-500 ease-out${
                    indeterminate || percent === null ? ' animate-pulse' : ''}`}
                style={{ width: indeterminate || percent === null ? '35%' : `${Math.max(2, percent)}%` }}
            />
        </div>
    );
}

export default function UpdateReadyBanner() {
    const [state, setState] = useState<UpdateState | null>(null);
    const [busy, setBusy] = useState(false);
    const [said, setSaid] = useState('');
    const [dismissed, setDismissed] = useState(false);
    const [showWhere, setShowWhere] = useState(false);
    const timer = useRef<number | null>(null);

    const bridge = (window as any)?.examClient?.app as Bridge | undefined;

    const refresh = useCallback(async () => {
        if (!bridge?.updateStatus) return null;
        try {
            const next = await bridge.updateStatus();
            setState(next);
            return next;
        } catch {
            return null;    // the EXE answers again on the next tick
        }
    }, [bridge]);

    // The poll rate follows what the updater is doing: re-scheduled after every
    // answer rather than fixed up front, so a download that starts between two
    // ticks speeds the polling up straight away instead of at the next minute.
    useEffect(() => {
        if (!isElectronServerRuntime() || !bridge?.updateStatus) return;
        let stopped = false;
        const tick = async () => {
            const next = await refresh();
            if (stopped) return;
            const active = BUSY_PHASES.has(String(next?.phase || ''));
            timer.current = window.setTimeout(tick, active ? POLL_BUSY_MS : POLL_IDLE_MS);
        };
        void tick();
        return () => {
            stopped = true;
            if (timer.current) window.clearTimeout(timer.current);
        };
    }, [bridge, refresh]);

    if (!isElectronServerRuntime() || !state || dismissed) return null;

    const phase = String(state.phase || '');
    const ready = phase === 'staged' || Boolean(state.can_install_manually);
    const working = BUSY_PHASES.has(phase);
    // Idle, or already up to date: a banner that is always there is a banner
    // nobody reads.
    if (!ready && !working) return null;

    const version = state.available_version || 'a new version';
    const pct = typeof state.percent === 'number' ? state.percent : null;
    const where = state.staged_installer || state.pending_dir || '';

    const install = async () => {
        if (!bridge?.installUpdate) return;
        setBusy(true);
        setSaid('Starting the installer… Windows will ask for permission.');
        try {
            const res = await bridge.installUpdate({ elevate: true });
            if (res?.ok === false) {
                setSaid(res.error || 'The installer could not be started.');
                setBusy(false);
                return;
            }
            setSaid('The installer is running. This window will close while it finishes.');
            void refresh();
        } catch (e: any) {
            setSaid(e?.message || 'The installer could not be started.');
            setBusy(false);
        }
    };

    // ── something is happening: say which, and show it moving ──────────────
    if (working && !ready) {
        const title = phase === 'checking' ? 'Checking for updates…'
            : phase === 'installing' ? `Installing update ${version}…`
            : `Downloading update ${version}`;
        // Only a download knows its size; checking and installing do not, so
        // they get a moving bar rather than a false percentage.
        const sized = phase === 'downloading' && Boolean(state.total_mb);
        return (
            <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-semibold">{title}</span>
                    {sized && (
                        <span className="tabular-nums text-slate-600 dark:text-slate-300">
                            {pct !== null ? `${pct}%` : ''}
                            {state.total_mb ? ` · ${state.downloaded_mb} of ${state.total_mb} MB` : ''}
                        </span>
                    )}
                </div>
                <Bar percent={sized ? pct : null} indeterminate={!sized} />
                <p className="mt-1.5 text-xs text-slate-600 dark:text-slate-400">
                    {phase === 'installing'
                        ? 'The Institute EXE will close while it finishes. Do not switch the computer off.'
                        : 'The lab is unaffected — it installs only when you choose.'}
                    {where && phase === 'downloading' ? ' Saving to this computer; nothing is lost if it stops.' : ''}
                </p>
                {where && (
                    <p className="mt-1 break-all font-mono text-[11px] text-slate-500 dark:text-slate-400">{where}</p>
                )}
            </div>
        );
    }

    // ── downloaded and waiting for a person ────────────────────────────────
    return (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-500/40 dark:bg-amber-500/10">
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[260px] text-sm text-amber-900 dark:text-amber-100">
                    <p className="font-semibold">
                        Update {version} is downloaded and ready to install.
                        {state.current_version ? (
                            <span className="ml-1 font-normal text-amber-800 dark:text-amber-200/90">
                                (this computer is on {state.current_version})
                            </span>
                        ) : null}
                    </p>
                    {/* 100% of the download is done; that is the whole point of
                        this state, and showing it stops "ready" reading as
                        "nothing has happened yet". */}
                    <Bar percent={100} />
                    <p className="mt-1.5 text-amber-800 dark:text-amber-200/90">
                        {state.total_mb ? `All ${state.total_mb} MB are already on this computer — nothing will be downloaded again. ` : 'Everything is already on this computer. '}
                        {state.admin_rights_required
                            // Worth saying it is the LAST time: the installer moves the app
                            // out of Program Files into this user's own folder, after which
                            // updates install silently with no prompt. Without that, an
                            // institute reasonably assumes every update will need somebody
                            // standing at the machine.
                            ? 'This copy sits in Program Files, so Windows asks for administrator permission once to move it. '
                              + 'Approve the prompt and it installs by itself — no setup window. '
                              + 'After this move, updates install on their own and will never ask again. '
                            : 'It installs by itself and restarts — nothing to click. '}
                        The Institute EXE closes while it installs, which takes about a minute.
                    </p>
                    {state.install_blocked && (
                        <p className="mt-1 text-amber-800 dark:text-amber-200/90">{state.install_blocked}</p>
                    )}
                    {typeof state.install_failures === 'number' && state.install_failures > 0 && (
                        <p className="mt-1 text-xs text-amber-800 dark:text-amber-200/90">
                            Tried to install on its own {state.install_failures} time
                            {state.install_failures === 1 ? '' : 's'} and was refused.
                        </p>
                    )}
                    {said && <p className="mt-1 font-medium">{said}</p>}
                    {where && (
                        <p className="mt-1.5 text-xs">
                            <button
                                type="button"
                                onClick={() => setShowWhere((v) => !v)}
                                className="underline underline-offset-2"
                            >
                                {showWhere ? 'Hide where it is saved' : 'Where is it saved?'}
                            </button>
                        </p>
                    )}
                    {showWhere && where && (
                        <p className="mt-1 break-all rounded bg-amber-100/70 p-2 font-mono text-[11px] text-amber-900 dark:bg-amber-500/10 dark:text-amber-100">
                            {where}
                            <span className="mt-1 block font-sans text-amber-800 dark:text-amber-200/90">
                                You can run this file yourself if the button does not work.
                            </span>
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={install}
                        disabled={busy}
                        className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
                    >
                        {busy ? 'Starting…' : 'Install now'}
                    </button>
                    <button
                        type="button"
                        onClick={() => setDismissed(true)}
                        className="rounded-md border border-amber-300 px-3 py-1.5 text-sm text-amber-900 hover:bg-amber-100 dark:border-amber-500/40 dark:text-amber-100 dark:hover:bg-amber-500/20"
                        title="It will be offered again next time the portal opens"
                    >
                        Later
                    </button>
                </div>
            </div>
        </div>
    );
}
